import { useCallback, useEffect, useState } from "react";
import { StaticDatePicker } from "@mui/x-date-pickers-pro";
import { Button, Menu, MenuItem } from "@mui/material";
import PropTypes from "prop-types";

import TodayIcon from "src/components/icons/TodayIcon";
import { NextIcon, PrevIcon } from "src/components/icons/NavIcons";
import ExpandCollapseIcon from "src/components/icons/purposed/ExpandCollapseIcon";

function FPTilauksetPvmSelector({
  showNextDayButton = false,
  showPrevDayButton = false,
  showTodayButton = false,
  disabled = false,
  setDates: setDatesProp,
  showPrev7DaysButton = true,
  showPrev30DaysButton = true,
  initialDate = null,
}) {
  const [startDate, setStartDate] = useState(new Date());
  // eslint-disable-next-line no-unused-vars
  const [endDate, setEndDate] = useState(null);
  const [buttonString, setButtonString] = useState("Tänään");
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (initialDate) {
      setDates(initialDate, initialDate);
    } else {
      // set endDate to 7 days from startDate
      const _startDate = new Date(startDate);
      _startDate.setDate(_startDate.getDate() - 7);
      setDates(_startDate, null);
    }
  }, []);

  const open = Boolean(anchorEl);

  const setDates = useCallback(
    (_startDate, _endDate) => {
      let str = "";
      if (_endDate == null) {
        str = "Alkaen " + _startDate.toLocaleDateString();
        // set endDate to 1 year from startDate
        _endDate = new Date(_startDate);
        _endDate.setFullYear(_endDate.getFullYear() + 1);
      } else if (_endDate == _startDate) str = _startDate.toLocaleDateString();
      else str = _startDate.toLocaleDateString() + " - " + _endDate.toLocaleDateString();
      setButtonString(str);
      setStartDate(_startDate);
      setEndDate(_endDate);
      setDatesProp({ startDate: _startDate, endDate: _endDate });
    },
    [setDatesProp]
  );

  const handleFilterButtonClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleViimClick = useCallback(
    (days) => {
      const viimdate = new Date();
      viimdate.setDate(viimdate.getDate() - days);
      setAnchorEl(null);
      setDates(viimdate, null);
    },
    [setDates]
  );

  const handlePvmChange = useCallback(
    (date) => {
      setButtonString(date.toLocaleDateString());
      setAnchorEl(null);
      setDates(date, date);
    },
    [setDates]
  );

  const handlePrevDayButtonClick = useCallback(() => {
    const _sDate = new Date(startDate);
    _sDate.setDate(_sDate.getDate() - 1);
    setDates(_sDate, _sDate);
  }, [startDate, setDates]);

  const handleNextDayButtonClick = useCallback(() => {
    const _sDate = new Date(startDate);
    _sDate.setDate(_sDate.getDate() + 1);
    setDates(_sDate, _sDate);
  }, [startDate, setDates]);

  const handleTodayButtonClick = useCallback(() => {
    const newDate = new Date();
    setDates(newDate, newDate);
  }, [setDates]);

  return (
    <>
      <div>
        <div className="spaceBetween" style={{ gap: 6 }}>
          {showPrevDayButton && (
            <Button
              variant="outlined"
              onClick={handlePrevDayButtonClick}
              disabled={disabled}
              sx={{ opacity: disabled ? 0.5 : 1, height: "50px" }}>
              <PrevIcon />
            </Button>
          )}
          <Button
            variant="outlined"
            id="demo-positioned-button"
            onClick={handleFilterButtonClick}
            sx={{ height: "50px" }}
            disabled={disabled}>
            {buttonString}

            <ExpandCollapseIcon sx={{ opacity: disabled ? 0.5 : 1 }} />
          </Button>
          {showTodayButton && (
            <Button
              variant="outlined"
              onClick={handleTodayButtonClick}
              sx={{ height: "50px", opacity: disabled ? 0.5 : 1 }}
              disabled={disabled}>
              <TodayIcon withText />
            </Button>
          )}
          {showNextDayButton && (
            <Button
              variant="outlined"
              onClick={handleNextDayButtonClick}
              disabled={disabled}
              sx={{ opacity: disabled ? 0.5 : 1, height: "50px" }}>
              <NextIcon />
            </Button>
          )}
        </div>
        <Menu
          id="demo-positioned-menu"
          aria-labelledby="demo-positioned-button"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}>
          {showPrev7DaysButton && (
            <MenuItem>
              <Button fullWidth variant="outlined" onClick={() => handleViimClick(7)}>
                Viimeiset 7 päivää
              </Button>
            </MenuItem>
          )}
          {showPrev30DaysButton && (
            <MenuItem>
              <Button fullWidth variant="outlined" onClick={() => handleViimClick(30)}>
                Viimeiset 30 päivää
              </Button>
            </MenuItem>
          )}
          <MenuItem>
            <StaticDatePicker
              okLabel=""
              showToolbar={false}
              closeOnSelect
              variant="text"
              disabled={false}
              inputFormat="dd.MM.yyyy"
              onChange={handlePvmChange}
              value={startDate}
              slotProps={{ textField: { variant: "standard" } }}
              minDate={new Date(2023, 3, 1)}
              onError={(reason, value) => {
                console.log(reason, value);
              }}
              displayStaticWrapperAs="desktop"
              sx={{ padding: 0 }}
            />
          </MenuItem>
        </Menu>
      </div>
    </>
  );
}

FPTilauksetPvmSelector.propTypes = {
  disabled: PropTypes.bool,
  showNextDayButton: PropTypes.bool,
  showPrevDayButton: PropTypes.bool,
  showTodayButton: PropTypes.bool,
  setDates: PropTypes.func,
  showPrev7DaysButton: PropTypes.bool,
  showPrev30DaysButton: PropTypes.bool,
  initialDate: PropTypes.instanceOf(Date),
};

export default FPTilauksetPvmSelector;
